import { useEffect, useState } from 'react';
import { fetchLlamada } from '../services/llamadas';
import type { LlamadaRow } from '../types/llamadas';
import { _claveDe, _exponer, type DetalleGuardado } from '../lib/llamadas/detalle';

// Fila completa (con transcripción) para el detalle — carga perezosa (v2).
// Se recarga cuando cambia la llamada seleccionada o su actualizado_en (un
// UPDATE realtime de la seleccionada refresca la conversación).
export function useLlamadaCompleta(
  id: string | null,
  actualizadoEn: string | null
): { completa: LlamadaRow | null; fresco: boolean; cargando: boolean; fallo: boolean } {
  // La fila se guarda JUNTO al id que la pidió, y solo se expone si coinciden.
  // Antes se guardaba sola, así que al cambiar de llamada seguía devolviendo la
  // ANTERIOR mientras la nueva estaba en vuelo. Con la feature base eso era «no
  // se marca ningún parte»; con el change 001 decide el modo entero de la
  // pantalla y qué puerta se consulta, así que enseñaba las revisiones de la
  // puerta de la llamada anterior bajo la llamada nueva. Hallazgo P1 del gate,
  // los dos revisores.
  const [dato, setDato] = useState<DetalleGuardado | null>(null);
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    if (!id) {
      setDato(null);
      setCargando(false);
      setFallo(false);
      return;
    }
    let vigente = true;
    setCargando(true);
    setFallo(false);
    fetchLlamada(id)
      .then((fila) => {
        if (!vigente) return;
        setDato({ id, clave: _claveDe(id, fila, actualizadoEn), fila });
        setCargando(false);
      })
      .catch(() => {
        if (!vigente) return;
        // `fallo` explícito: sin él, «no lo he podido consultar» era
        // indistinguible de «esta llamada no identificó puerta», y la pantalla
        // afirmaba lo segundo sin saberlo.
        setDato({ id, clave: _claveDe(id, null, actualizadoEn), fila: null });
        setCargando(false);
        setFallo(true);
      });
    return () => {
      vigente = false;
    };
  }, [id, actualizadoEn]);

  // DOS cosas distintas, y confundirlas fue el fallo:
  //
  //   · `completa` sirve para pintar la CONVERSACIÓN, y ahí una versión un poco
  //     vieja de la MISMA llamada vale: vaciarla en cada UPDATE de Realtime haría
  //     desaparecer la transcripción un instante en cada refresco, que sería una
  //     regresión en `panel-atencion`.
  //   · `fresco` dice si el detalle es de ESTA versión de la llamada, y es lo que
  //     hay que exigir para decidir de qué puerta va.
  //
  // La distinción no es teórica. Rechacé este hallazgo en la segunda vuelta
  // diciendo que `cargando` tapaba la ventana, y la tercera me corrigió con
  // razón: cuando llega un UPDATE de Realtime, el render que sigue al cambio de
  // prop pasa ANTES de que corra el efecto, así que `cargando` todavía es
  // `false` y la fila vieja sale como actual. `fresco` se deriva de props y
  // estado en el propio render, sin efecto de por medio, así que no tiene esa
  // ventana.
  return {
    ..._exponer(id, actualizadoEn, dato),
    // Mientras el id no coincida, el dato es de otra llamada: eso es «todavía no
    // lo sé», no «no hay».
    cargando: cargando || (id != null && dato?.id !== id),
    fallo,
  };
}
